import {Platform} from 'react-native'
import AsyncStorage from '@react-native-async-storage/async-storage'

const DRAFT_KEY = 'tellustv:upload-draft:v1'

export type UploadDraftSegment = {
  index: number
  uri: string
  cid?: string
  durationMs: number
  mimeType?: string
  byteLength?: number
}

export type UploadDraft = {
  version: 1
  fileName: string
  fileSize: number
  title: string
  description?: string
  createdAt: number
  segmentsTotal?: number
  segmentSeconds?: number
  uploadedSegments?: UploadDraftSegment[]
}

function safeParse<T>(raw: string | null): T | undefined {
  if (!raw) return undefined
  try {
    return JSON.parse(raw) as T
  } catch {
    return undefined
  }
}

async function getItem<T>(key: string): Promise<T | undefined> {
  if (Platform.OS === 'web') {
    if (typeof window === 'undefined' || !window.localStorage) return undefined
    return safeParse<T>(window.localStorage.getItem(key))
  }
  const raw = await AsyncStorage.getItem(key)
  return safeParse<T>(raw)
}

async function setItem<T>(key: string, value: T): Promise<void> {
  const raw = JSON.stringify(value)
  if (Platform.OS === 'web') {
    if (typeof window === 'undefined' || !window.localStorage) return
    try {
      window.localStorage.setItem(key, raw)
    } catch {
      return
    }
    return
  }
  await AsyncStorage.setItem(key, raw)
}

async function removeItem(key: string): Promise<void> {
  if (Platform.OS === 'web') {
    if (typeof window === 'undefined' || !window.localStorage) return
    window.localStorage.removeItem(key)
    return
  }
  await AsyncStorage.removeItem(key)
}

export async function loadUploadDraft(): Promise<UploadDraft | null> {
  return (await getItem<UploadDraft>(DRAFT_KEY)) || null
}

export async function saveUploadDraft(draft: UploadDraft): Promise<void> {
  await setItem(DRAFT_KEY, draft)
}

export async function clearUploadDraft(): Promise<void> {
  await removeItem(DRAFT_KEY)
}
